import React from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
// import { Separator } from '@/components/ui/separator';
// Import icons if desired (e.g., from lucide-react or custom SVGs)
import GoogleIcon from '@/components/icons/Google';
import { UserIcon } from 'lucide-react';
import { useAppStore } from '@/hooks/useAppStore';
import { useTranslation } from '@/lib/translate';
import { useTheme } from '@/components/themes/ThemeProvider';

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const user = useAppStore((state) => state.user);
  const preferredLanguage = useAppStore((state) => state.preferredLanguage);
  const [loading, setLoading] = React.useState(false);
  const t = useTranslation(preferredLanguage);
  const isGuest = user?.provider == 'internal';
  const from = location.state?.from?.pathname || "/chat/default"; // Where to redirect after login
  const clientConfig = useAppStore((state) => state.clientConfig);
  const { resolvedTheme } = useTheme();

  const logoSrc = resolvedTheme === 'dark' && clientConfig?.config?.logoDark
    ? clientConfig.config.logoDark
    : clientConfig?.config?.logo;

  // Google OAuth handler
  const handleGoogleLogin = () => {
    setLoading(true);
    // Redirect to backend's Google OAuth start endpoint
    window.location.href = `/auth/google/login?returnTo=${encodeURIComponent(searchParams.get("returnTo") || "")}`;
    // After successful OAuth flow, backend redirects back with JWT in URL and is handled by AuthExchangePage.tsx
  };

  const handleLineLogin = () => {
    console.log("Attempting Line Login...");
    // Similar flow as Google Login
    alert("Line Login not implemented yet.");
  };

  const handleGuestLogin = () => {
    setLoading(true);
    if (isGuest) {
      navigate("/");
      return;
    }
    navigate('/guest-login');
  };

  const handleSignup = () => {
    setLoading(true);
    navigate(`/signup?returnTo=${encodeURIComponent(searchParams.get("returnTo") || "")}`, { state: { from } }); // Go to signup page
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-900 p-4 login-container">
      <Card className="w-full max-w-sm bg-neutral-900/30 border-neutral-700 text-white rounded-4xl border-0">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl uppercase tracking-[4px] text-chotto-gold items-center flex justify-center">
            <img src={logoSrc} width="230" />
          </CardTitle>
          <p className="text-neutral-100 text-sm">{t("talk a bit, connect a lot")}</p>
        </CardHeader>
        <CardContent className="space-y-4 mt-6">
          <Button onClick={handleGoogleLogin} className="w-full bg-white text-black hover:bg-neutral-200" disabled={loading}>
            <GoogleIcon className="mr-2 h-5 w-5" />
            {t("Sign in with Google")}
          </Button>
          {/* <Button onClick={handleLineLogin} className="w-full bg-[#00B900] text-white hover:bg-[#00a300]">
            <LineIcon className="mr-2 h-5 w-5" />
            Sign in with Line
          </Button> */}
          <Button onClick={handleGuestLogin} className="w-full text-black bg-green-300 hover:bg-green-300/90" disabled={loading}>
            <UserIcon className="mr-2 h-5 w-5" /> {isGuest ? t("Continue as Guest") : t("Sign in as Guest")}
          </Button>

          {/* Optional: Add Email/Password fields later */}
          {/* <p className="text-center text-sm text-neutral-400">Login with email coming soon!</p> */}

          <p className="text-center text-sm text-neutral-100 mt-2 mb-10">
            {t("Don't have an account?")}{' '}
            <Button variant="link" className="p-0 h-auto text-neutral-100" onClick={handleSignup} disabled={loading}>
              {t("Sign Up")}
            </Button>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
